"use client"

import type React from "react"
import { useEffect, useState, useRef } from "react"
import api from "../../libs/axios"
import { toast } from "react-hot-toast"
import { VendorList } from "./list"
import type { Filters as VendorFilters } from "./list"
import { AddVendorOffcanvas } from "./add"
import { EditVendorOffcanvas } from "./edit"
import { PaginationType } from "../../utils/pagination.utils"
import { useDebounce } from "../../hooks/useDebounce"

export interface VendorRepresentative {
  id?: number
  contact_person: string
  country_code?: string
  contact_number: string
  designation: string
  designation_id: number | null
  representative_location: string
  email?: string | null
  vendor_id?: number
  created_at?: string
  updated_at?: string
}

export interface Vendor {
  id: number
  vendor_full_name: string
  vendor_short_name: string
  vendor_code?: string | null
  address: string
  gst_number: string
  pan_number?: string | null
  bank_account_number?: string | null
  bank_ifsc_code?: string | null
  bank_name?: string | null
  bank_address?: string | null
  state_circle_id: number
  vendor_type?: string
  is_active: boolean
  created_by: number
  suspended_by?: number | null
  suspended_at?: string | null
  created_at: string
  updated_at: string
  representatives?: VendorRepresentative[]
  project_associations?: Array<{ id: number; vendor_id: number; project_id: number; is_active: boolean; project?: { id: number; project_name?: string } }>
  activity_associations?: Array<{ id: number; vendor_id: number; activity_id: number; is_active: boolean; activity?: { id: number; activity_name?: string } }>
}

const DEFAULT_PAGINATION: PaginationType = {
  offset: 0,
  page: 1,
  limit: 10,
  total_pages: 0,
  total_count: 0,
  has_next_page: false,
  has_prev_page: false,
  next_page: null,
  prev_page: null,
}

export const VendorManagement: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [pagination, setPagination] = useState<PaginationType>(DEFAULT_PAGINATION)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  // Debounced search — prevents API call on every keystroke
  const debouncedSearch = useDebounce(searchTerm, 400)

  const [advancedFilters, setAdvancedFilters] = useState<VendorFilters>({})
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' }>({
    field: 'created_at',
    direction: 'DESC',
  })

  const [showAddVendor, setShowAddVendor] = useState(false)
  const [showEditVendor, setShowEditVendor] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null)

  const fetchVendors = async (
    search = "",
    page = 1,
    status?: string,
    limitValue?: number,
    filters: VendorFilters = advancedFilters,
    sort = sortConfig,
  ) => {
    setLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const params: Record<string, string | number | boolean> = {
        page,
        limit: effectiveLimit,
      }

      // Effective search: prefer explicit search argument, fallback to filters.search
      const effectiveSearch = search.trim() || (filters.search ? filters.search.trim() : "")
      if (effectiveSearch) {
        params.search = effectiveSearch
      }

      // Effective status: filters.status overrides top-level statusFilter if provided
      const rawStatus = (filters.status || status) && (filters.status || status) !== "all"
        ? (filters.status || status)
        : undefined
      if (rawStatus) {
        params.status = rawStatus.toUpperCase()
      }

      // Field-specific filters
      if (filters.name && filters.name.trim()) {
        // Map UI "Vendor Name" to vendor_full_name filter
        params.vendor_full_name = filters.name.trim()
      }

      if (filters.gst_number && filters.gst_number.trim()) {
        params.gst_number = filters.gst_number.trim()
      }

      if (filters.address && filters.address.trim()) {
        params.address = filters.address.trim()
      }

      // Location (simple mapping based on existing Filter fields)
      if (filters.state_id && filters.state_id.trim()) {
        // Backend expects state_circle_id or state_circle_id_list; map state_id to state_circle_id
        params.state_circle_id = filters.state_id.trim()
      }

      if (filters.district_id && filters.district_id.trim()) {
        params.district_id = filters.district_id.trim()
      }

      // Date ranges
      if (filters.created_from) {
        params.created_at_from = filters.created_from
      }

      if (filters.created_to) {
        params.created_at_to = filters.created_to
      }

      // Associations
      if (filters.project_id && filters.project_id.trim()) {
        params.project_id = filters.project_id.trim()
      }

      if (filters.activity_id && filters.activity_id.trim()) {
        params.activity_id = filters.activity_id.trim()
      }

      if (filters.has_representatives) {
        // "true" / "false" as strings from UI; pass through directly
        params.has_representatives = filters.has_representatives === "true"
      }

      // Suspension flag
      if (filters.is_suspended) {
        params.is_suspended = filters.is_suspended === "true"
      }

      // Sorting
      if (sort?.field) {
        params.sort_by = sort.field
        params.sort_order = sort.direction
      }

      const response = await api.get("/vendor/list", { params })

      if (response.data.success) {
        setVendors(response.data.data.vendors || [])
        const apiPagination = response.data.data.pagination
        console.log("API Pagination Response:", apiPagination)
        const totalPages = apiPagination?.total_pages ?? apiPagination?.totalPages ?? 0
        const totalCount = apiPagination?.total_count ?? apiPagination?.total ?? apiPagination?.totalCount ?? 0
        const currentPage = Math.min(apiPagination?.page ?? page, Math.max(totalPages, 1))

        setPagination(
          apiPagination
            ? {
              offset: apiPagination.offset ?? 0,
              page: currentPage,
              limit: apiPagination.limit ?? effectiveLimit,
              total_pages: totalPages,
              total_count: totalCount,
              has_next_page: apiPagination.has_next_page ?? false,
              has_prev_page: apiPagination.has_prev_page ?? false,
              next_page: apiPagination.next_page ?? null,
              prev_page: apiPagination.prev_page ?? null,
            }
            : {
              offset: 0,
              page: Math.min(page, 1),
              limit: effectiveLimit,
              total_pages: 0,
              total_count: 0,
              has_next_page: false,
              has_prev_page: false,
              next_page: null,
              prev_page: null,
            },
        )
      } else {
        toast.error(response.data.message || "Failed to fetch vendors")
      }
    } catch (error: any) {
      console.error("Error fetching vendors:", error)
      toast.error(error?.response?.data?.message || "Failed to fetch vendors")
    } finally {
      setLoading(false)
    }
  }

  const fetchVendorById = async (vendorId: number) => {
    try {
      const response = await api.get(`/vendor/${vendorId}`)
      if (response.data.success) {
        return response.data.data.vendor as Vendor
      }
      toast.error(response.data.message || "Failed to fetch vendor details")
      return null
    } catch (error: any) {
      console.error("Error fetching vendor by ID:", error)
      toast.error(error?.response?.data?.message || "Failed to fetch vendor details")
      return null
    }
  }

  const handleCreateVendor = async (vendorData: any) => {
    setActionLoading(true)
    try {
      const response = await api.post("/vendor/create", vendorData)
      if (response.data.success) {
        toast.success("Vendor created successfully")
        setShowAddVendor(false)
        await fetchVendors()
      } else {
        toast.error(response.data.message || "Failed to create vendor")
      }
    } catch (error: any) {
      console.error("Error creating vendor:", error)
      toast.error(error?.response?.data?.message || "Failed to create vendor")
    } finally {
      setActionLoading(false)
    }
  }

  const handleUpdateVendor = async (vendorData: any) => {
    setActionLoading(true)
    try {
      const response = await api.put(`/vendor/${vendorData.id}`, vendorData)
      if (response.data.success) {
        toast.success("Vendor updated successfully")
        setShowEditVendor(false)
        setSelectedVendor(null)
        await fetchVendors()
      } else {
        toast.error(response.data.message || "Failed to update vendor")
      }
    } catch (error: any) {
      console.error("Error updating vendor:", error)
      toast.error(error?.response?.data?.message || "Failed to update vendor")
    } finally {
      setActionLoading(false)
    }
  }

  const handleToggleStatus = async (vendor: Vendor) => {
    setActionLoading(true)
    try {
      const response = await api.patch(`/vendor/${vendor.id}/status`, {
        status: vendor.is_active ? "suspended" : "active",
      })

      if (response.data.success) {
        toast.success(`Vendor ${vendor.is_active ? "suspended" : "activated"} successfully`)
        await fetchVendors(searchTerm, pagination.page, statusFilter, undefined, advancedFilters)
      } else {
        toast.error(response.data.message || "Failed to update vendor status")
      }
    } catch (error: any) {
      console.error("Error toggling vendor status:", error)
      toast.error(error?.response?.data?.message || "Failed to update vendor status")
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteVendor = async (vendorId: number) => {
    setActionLoading(true)
    try {
      const response = await api.delete(`/vendor/${vendorId}`)
      if (response.data.success) {
        toast.success("Vendor deleted successfully")
        await fetchVendors(searchTerm, pagination.page, statusFilter, undefined, advancedFilters)
      } else {
        toast.error(response.data.message || "Failed to delete vendor")
      }
    } catch (error: any) {
      console.error("Error deleting vendor:", error)
      toast.error(error?.response?.data?.message || "Failed to delete vendor")
    } finally {
      setActionLoading(false)
    }
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    // Actual API call is triggered by the debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchVendors(debouncedSearch, 1, statusFilter, undefined, advancedFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value)
    fetchVendors(searchTerm, 1, value, undefined, advancedFilters)
  }

  const handlePageChange = (page: number) => {
    fetchVendors(searchTerm, page, statusFilter, undefined, advancedFilters)
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchVendors(searchTerm, 1, statusFilter, limit, advancedFilters)
  }

  const handleAdvancedFilter = (filters: VendorFilters) => {
    setAdvancedFilters(filters)
    // Apply new filters from page 1 while keeping current search and status
    fetchVendors(searchTerm, 1, statusFilter, undefined, filters)
  }

  const handleSort = (field: string, direction: 'ASC' | 'DESC') => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchVendors(searchTerm, pagination.page, statusFilter, undefined, advancedFilters, sort)
  }

  const handleEditVendor = async (vendor: Vendor) => {
    const fullVendor = await fetchVendorById(vendor.id)
    if (fullVendor) {
      setSelectedVendor(fullVendor)
      setShowEditVendor(true)
    }
  }

  const handleCloseOverlays = () => {
    if (actionLoading) return
    setShowAddVendor(false)
    setShowEditVendor(false)
    setSelectedVendor(null)
  }

  useEffect(() => {
    fetchVendors()
  }, [])

  return (
    <div className="">
      <VendorList
        vendors={vendors}
        pagination={pagination}
        loading={loading}
        actionLoading={actionLoading}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        onSearch={handleSearch}
        onStatusChange={handleStatusFilterChange}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onAddVendor={() => setShowAddVendor(true)}
        onEditVendor={handleEditVendor}
        onToggleStatus={handleToggleStatus}
        onDeleteVendor={handleDeleteVendor}
        onAdvancedFilter={handleAdvancedFilter}
        onSort={handleSort}
      />

      <AddVendorOffcanvas
        isOpen={showAddVendor}
        onClose={handleCloseOverlays}
        onSubmit={handleCreateVendor}
        loading={actionLoading}
      />

      <EditVendorOffcanvas
        isOpen={showEditVendor}
        onClose={handleCloseOverlays}
        onSubmit={handleUpdateVendor}
        vendor={selectedVendor}
        loading={actionLoading}
      />
    </div>
  )
}
